import { HttpErrorResponse } from '@angular/common/http';
import {
  Component,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import {
  ConsentTypes,
  FLOW_TYPE,
} from '@credit-acceptance/shareable-utilities';
import { FullStory } from '@fullstory/browser';
import { adobeAnalytics } from 'adobe-analytics';
import {
  BehaviorSubject,
  catchError,
  forkJoin,
  map,
  merge,
  Observable,
  of,
  retry,
  startWith,
  Subject,
  take,
  tap,
  timeout,
  TimeoutError,
} from 'rxjs';

import { PREQUAL_SEO } from '../../assets/constants';
import { IdentificationComponent } from '../identification/identification.component';
import { SSNError } from '../interfaces/common';
import { ConsentTextInfoRestService } from '../services/consent-test-info-rest.service';
import { LocalService } from '../services/local.service';
import { PrequalService } from '../services/prequal.service';
import { Unsubscribe } from '../shared/classes/unsubscribe.class';
import { FS_EVENTS, FS_LABELS } from '../shared/constants/fsevents';
import { UTMModel } from '../shared/models/utmModel';
import { isWwwAiChatChallenger } from '../shared/utils/ab-test';
import { uuid } from '../shared/utils/uuid';

declare const FS: typeof FullStory;

const initMinutesLeft = 10;
const consentLoadRetryCount = 1;
const consentLoadRetryDelayMs = 1000;
const consentLoadTimeoutMs = 5000;
const initSecondsLeft = 0;

@Component({
  selector: 'capq-main-container',
  templateUrl: './main-container.component.html',
  styleUrls: ['./main-container.component.scss'],
  standalone: false,
})
export class MainContainerComponent
  extends Unsubscribe
  implements OnInit, OnDestroy
{
  sessionRemainingTime = 30 * 60;
  minutesLeft = initMinutesLeft;
  secondsLeft = initSecondsLeft;
  remainingTimeInterval: ReturnType<typeof setInterval> | null = null;
  extendedSessionLength = 10 * 60;
  showPopup = false;
  forceReload = false;
  adobeScriptLoaded = false;
  adobeLoadingTime = 0;
  submissionFailureReason = '';
  preventDefaultOnWindowUnload = false;
  errorMessage = '';
  errorType = '';
  userDealerIds: string[] = [];

  activeStep$: Observable<number>;
  activeStepUpdate = new Subject<number>();

  sessionExpirationTime$ = new BehaviorSubject<string>('');
  private readonly allConsentsGiven = new BehaviorSubject({
    identification: false,
    summary: false,
    sms: false,
  });
  allConsentsGiven$ = this.allConsentsGiven
    .asObservable()
    .pipe(map((consents) => consents.identification && consents.summary));

  get smsConsentGiven(): boolean {
    return this.allConsentsGiven.value.sms;
  }

  /**
   * SVRS-49895: the SMS text-consent checkbox is shown to everyone except the
   * WWW AI Chat challenger group, who get the on-site AI chatbot instead.
   */
  get showSmsConsent(): boolean {
    return !isWwwAiChatChallenger();
  }

  @HostListener('window:beforeunload', ['$event']) unloadHandler(event: Event) {
    if (this.preventDefaultOnWindowUnload && !this.forceReload) {
      event.preventDefault();
    }
  }

  @ViewChild('identification')
  identificationComponent!: IdentificationComponent;

  constructor(
    readonly localService: LocalService,
    private readonly prequalService: PrequalService,
    private readonly metaService: Meta,
    private readonly titleService: Title,
    private readonly consentTextInfoRestService: ConsentTextInfoRestService,
    private readonly activatedRoute: ActivatedRoute
  ) {
    super();
    this.activeStep$ =
      this.retrieveRequiredAppDataAndSetupActiveStepObservable();

    // Set default prequal flow type if not already set, with sessionStorage check
    try {
      if (typeof window !== 'undefined' && window.sessionStorage) {
        if (sessionStorage.getItem('prequalFlowType') === null) {
          sessionStorage.setItem(
            'prequalFlowType',
            JSON.stringify({ flowType: FLOW_TYPE.OSM.toString() })
          );
        }
      }
    } catch (e) {
      // sessionStorage not available or error occurred, do nothing
    }

    //UTM code
    adobeAnalytics.init((val: boolean, lt: number) => {
      this.adobeLoadingTime = lt;
      this.adobeScriptLoaded = val;
    });

    let userSourceURL = '';
    if (window.location.href.toLowerCase().includes('utm_')) {
      userSourceURL = window.location.href;
    }

    let sourceStoredInLocal: UTMModel[] = JSON.parse(
      localStorage.getItem('userSource') || '[]'
    );

    if (sourceStoredInLocal.length) {
      const diffInDays = localService.getDiffInDays(
        sourceStoredInLocal[0]?.time
      );
      if (diffInDays > 270) {
        sourceStoredInLocal = [];
        localService.setData('userSource', JSON.stringify(sourceStoredInLocal));
      }
    }

    const encodeURl = btoa(userSourceURL);

    if (userSourceURL) {
      const currentTime = new Date().toISOString();
      sourceStoredInLocal.push({
        url: encodeURl,
        time: currentTime,
        updated: true,
      });

      localService.setData('userSource', JSON.stringify(sourceStoredInLocal));
    }
    //UTM code
  }

  ngOnInit(): void {
    const queryParams = this.activatedRoute.snapshot.queryParamMap;
    const dealers = queryParams.get('dealers');

    if (dealers) {
      this.userDealerIds = dealers.split(',');
    }

    this.setupAppSEOData(PREQUAL_SEO);
    // Set up the session expiration timer
    this.remainingTimeInterval = setInterval(() => {
      this.sessionRemainingTime -= 1;
      if (!this.sessionRemainingTime) {
        // no session time left, reset
        this.remainingTimeInterval && clearInterval(this.remainingTimeInterval);
        this.reload();
      }
      if (this.sessionRemainingTime <= 10 * 60) {
        this.minutesLeft = Math.floor(this.sessionRemainingTime / 60);
        this.secondsLeft = this.sessionRemainingTime % 60;

        // generate session expiration time string for display
        const minutesLeft = this.minutesLeft
          ? `${this.minutesLeft} minute${this.minutesLeft > 1 ? 's' : ''}`
          : '';
        const secondsLeft = `${this.secondsLeft} second${
          this.secondsLeft > 1 ? 's' : ''
        }`;
        this.sessionExpirationTime$.next(`${minutesLeft} ${secondsLeft}`);
        this.showPopup = true;
      }
    }, 1000);
  }

  retrieveRequiredAppDataAndSetupActiveStepObservable() {
    const loadStartedAt = Date.now();
    // Fixed per attempt (including retries) so the same id can be traced
    // end-to-end through backend/Dynatrace logs for that consent load.
    const correlationIds = {
      emailDisclosure: uuid(),
      productTerms: uuid(),
    };

    return merge(
      this.prequalService.errorTriggered.pipe(map(() => -1)),
      forkJoin([
        // fetch all required data the app needs before we can allow the user to begin interactions
        this.consentTextInfoRestService
          .getConsentByType(
            ConsentTypes.EMAIL_DISCLOSURE,
            correlationIds.emailDisclosure
          )
          .pipe(timeout(consentLoadTimeoutMs), take(1)),
        this.consentTextInfoRestService
          .getConsentByType(
            ConsentTypes.PRODUCT_TERMS,
            correlationIds.productTerms
          )
          .pipe(timeout(consentLoadTimeoutMs), take(1)),
      ]).pipe(
        retry({ count: consentLoadRetryCount, delay: consentLoadRetryDelayMs }),
        map(() => 1)
      ),
      this.activeStepUpdate
    ).pipe(
      tap((activeStep) => {
        this.preventDefaultOnWindowUnload = activeStep < 7;
      }),
      startWith(0),
      catchError((error) => {
        console.error('Failed to load required app data:', error);
        this.emitLoadErrorFsEvent(error, loadStartedAt, correlationIds);
        return of(-1);
      })
    );
  }

  private emitLoadErrorFsEvent(
    error: unknown,
    loadStartedAt: number,
    correlationIds: { emailDisclosure: string; productTerms: string }
  ) {
    if (typeof FS === 'undefined') {
      return;
    }
    const httpStatus =
      error instanceof HttpErrorResponse ? error.status : undefined;
    const isTimeout = error instanceof TimeoutError;
    const genericError = error as Partial<Error>;
    FS.event(FS_EVENTS.PREQUAL_LOAD_ERROR, {
      action_label: FS_LABELS.LOAD_ERROR,
      loadError: true,
      httpStatus: httpStatus ?? 0,
      // No HttpErrorResponse (status 0/undefined) usually means the request
      // never completed with a readable response, e.g. blocked by CORS.
      errorKind: isTimeout
        ? 'timeout'
        : httpStatus
        ? String(httpStatus)
        : 'no-status/cors',
      errorName: genericError?.name ?? 'unknown',
      errorMessage:
        (error instanceof HttpErrorResponse
          ? error.message
          : genericError?.message) ?? String(error),
      correlationIdEmailDisclosure: correlationIds.emailDisclosure,
      correlationIdProductTerms: correlationIds.productTerms,
      elapsedMs: Date.now() - loadStartedAt,
      online: navigator.onLine,
      connectionType: (navigator as any).connection?.effectiveType ?? 'unknown',
    });
  }

  setupAppSEOData(prequalifiedSEOData: any) {
    this.metaService.addTag({
      content: 'text/html; charset=UTF-8',
      httpEquiv: 'content-type',
    });
    this.metaService.addTag({
      name: 'facebook-domain-verification',
      content: 'mvxyneyuqg2hec8wzlwa6aluhfbrya',
    });
    this.metaService.addTag({
      content: prequalifiedSEOData.description,
      name: 'description',
    });
    this.titleService.setTitle(prequalifiedSEOData.title);
    this.metaService.addTag({
      name: 'canonical',
      content: window.location.href,
    });
  }

  override ngOnDestroy() {
    super.ngOnDestroy();
    this.remainingTimeInterval && clearInterval(this.remainingTimeInterval);
  }

  extend() {
    this.showPopup = false;
    this.minutesLeft = initMinutesLeft;
    this.secondsLeft = initSecondsLeft;
    this.sessionRemainingTime += this.extendedSessionLength;
  }
  reload() {
    this.forceReload = true;
    window.location.reload();
  }

  updateActiveStep(step: number) {
    this.activeStepUpdate.next(step);
    this.errorMessage = '';
    this.errorType = '';
  }

  updateFailureReason(reason: string) {
    this.submissionFailureReason = reason;
  }

  updateConsent(type: 'identification' | 'summary' | 'sms', consent: boolean) {
    this.allConsentsGiven.next({
      ...this.allConsentsGiven.value,
      [type]: consent,
    });
  }

  updateSSNError(ssnError: SSNError) {
    this.errorMessage = ssnError.message;
    this.errorType = ssnError.type;
    setTimeout(() => {
      const element = document.getElementById('error-subsection');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }, 500);
  }

  resetIdentificationConsent() {
    this.identificationComponent.resetConsentAgreement();
  }
}
